import type {FC} from 'react';
import {useState, useEffect} from 'react';
import {useQuery, keepPreviousData} from '@tanstack/react-query';
import type {GridSortModel, GridSelectionModel} from '@mui/x-data-grid';
import type {ProgramAdmissionRead} from 'api/v2';
import {DataGrid} from '@wellstone-solutions/web';
import {usePaginationState} from 'modules/datagrid';
import {programAdmissionsQueryOptions} from 'queries';
import {addToGroupColumns} from './columns';
import {SelectedRowChips} from './SelectedRowChips';
interface SelectMemberListProps {
  groupId: string;
  programId: string;
  searchTerm?: string;
  selectedRows: Array<ProgramAdmissionRead>;
  setSelectedRows: (rows: Array<ProgramAdmissionRead>) => void;
}

export const SelectMemberList: FC<SelectMemberListProps> = ({
  groupId,
  programId,
  searchTerm,
  selectedRows,
  setSelectedRows,
}) => {
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>(
    selectedRows.map((row) => row.id),
  );

  const defaultSortModel: GridSortModel = [
    {
      field: 'member__name',
      sort: 'asc',
    },
  ];

  const {
    page,
    setPage,
    pageSize,
    onPageSizeChange,
    ordering,
    onSortModelChange,
  } = usePaginationState({
    defaultSortModel,
  });

  const query = useQuery({
    ...programAdmissionsQueryOptions.list({
      programId,
      page,
      pageSize,
      ordering,
      ...(searchTerm && {search: searchTerm}),
    }),
    placeholderData: keepPreviousData,
  });

  const canSelectRow = (row: ProgramAdmissionRead) =>
    !row.member.groups.includes(groupId);

  const onSelect = (newSelectionModel: GridSelectionModel) => {
    if (!query.data?.results) {
      return;
    }

    setSelectionModel(newSelectionModel);

    // Find the newly selected row ids and corresponding program admission
    const newRowIds = new Set(
      newSelectionModel.filter((id) => !selectionModel.includes(id)),
    );
    const newRowObjs = query.data.results.filter((row) =>
      newRowIds.has(row.id),
    );

    // Filter deselected rows from previous state
    const stillSelected = selectedRows.filter((row) =>
      newSelectionModel.includes(row.id),
    );

    setSelectedRows([...stillSelected, ...newRowObjs]);
  };

  // Make sure that members on the current page who are already in the group appear selected
  // Checkbox is disabled through the isRowSelectable prop on DataGrid
  useEffect(() => {
    if (!query.data?.results) {
      return;
    }

    const existingMembers = query.data.results.flatMap((row) =>
      canSelectRow(row) ? [] : [row.id],
    );

    setSelectionModel((prev) => [...prev, ...existingMembers]);
  }, [query.data]);

  return (
    <>
      <SelectedRowChips
        selectedRows={selectedRows}
        onDelete={(chipId: string) => {
          onSelect(selectionModel.filter((rowId) => rowId !== chipId));
        }}
      />

      <DataGrid
        checkboxSelection
        keepNonExistentRowsSelected
        hideFooterSelectedRowCount
        loading={query.isFetching}
        rows={query.data?.results || []}
        rowCount={query.data?.count || 0}
        noRowsText={
          searchTerm ? `No members found for "${searchTerm}"` : 'No members'
        }
        columns={[
          addToGroupColumns.name,
          addToGroupColumns.groups,
          addToGroupColumns.startDate,
        ]}
        paginationMode="server"
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        initialState={{
          sorting: {
            sortModel: defaultSortModel,
          },
        }}
        page={page - 1}
        pageSize={pageSize}
        onPageChange={(newPage: number) => setPage(newPage + 1)}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={onSortModelChange}
        selectionModel={selectionModel}
        onSelectionModelChange={onSelect}
        isRowSelectable={({row}) => canSelectRow(row)}
      />
    </>
  );
};
